// Vendors
import React, { useState, useEffect } from 'react';
import qs from 'querystring';
import axios from 'axios';
import $ from 'jquery';

// Components
import {
  Appliances,
  Epat,
  ProjectForm,
  Messages,
  PropaneProvider,
  SurveyModal
} from './components';
import { Loader, DeleteProject } from '../components';

// Helpers
import { isCompletionDateCorrect } from '../components/helpers';

const projectDataDefaults = {
  construction_type: 'New',
  address: '',
  city: '',
  zipcode: '',
  completion_date: '',
  status: 'In Progress',
  document_epat: '0',
  epat_emissions: '',
  epat_emissions_baseline: '',
  epat_emissions_baseline_so2: '',
  epat_emissions_baseline_nox: '',
  epat_emissions_baseline_co2: '',
  epat_emissions_baseline_ch4: '',
  epat_emissions_baseline_n2o: '',
  epat_emissions_baseline_co2e: '',
  epat_emissions_alternative: '',
  epat_emissions_alternative_so2: '',
  epat_emissions_alternative_nox: '',
  epat_emissions_alternative_co2: '',
  epat_emissions_alternative_ch4: '',
  epat_emissions_alternative_n2o: '',
  epat_emissions_alternative_co2e: '',
  appliance_boiler: '',
  appliance_furnace: '',
  appliance_furnace_changed_from: '',
  appliance_hydronic_air_handler: '',
  appliance_combi_boiler: '',
  appliance_water_heater: '',
  appliance_hybrid_heat_pump: '',
  appliance_cooktop_range: '',
  appliance_cooktop_range_changed_from: '',
  appliance_oven: '',
  appliance_oven_changed_from: '',
  appliance_grill: '',
  appliance_clothes_dryer: '',
  appliance_clothes_dryer_changed_from: '',
  appliance_standby_generator: '',
  appliance_standby_generator_changed_from: '',
  appliance_fireplace_gas_log: '',
  appliance_fireplace_gas_log_changed_from: '',
  appliance_piping_propane_gas: '',
  appliance_piping_propane_gas_changed_from: '',
  appliance_total_incentives: '',
  pid: null,
  user_builder_w9: '', // deprecated
  has_uploaded_w9: '',
  gas_rep_company_name: '',
  gas_rep_representative: '',
  gas_rep_city: '',
  gas_rep_state: '',
  appliance_attestation: 0
};

const surveyDataDefaults = {
  building_cost: '',
  how_much_less: '',
  how_much_more: '',
  sell_speed: '',
  sell_cost: '',
  propane_barrier: [],
  propane_barrier_other: '',
  home_buyer_involvement: '',
  energy_decisions: [],
  environmental_benefits: '',
  energy_planning_analysis_tool: '',
  emissions_benefits: '',
  propane_construction_research_program: '',
  may_we_contact_you: ''
};

// Removes the iframe source on modal close, so the video stops playing.
if ($('#epat-modal__close').length) {
  $('#epat-modal__close').on('click', () => {
    const iframe = document.getElementById('epat-modal__video');
    const src = iframe.src;
    iframe.src = '';
    setTimeout(() => {
      iframe.src = src;
    }, 500);
  });
}

const ViewProject = () => {
  const [loading, setLoading] = useState(true);
  const [projectData, setProjectData] = useState(projectDataDefaults);
  const [surveyData, setSurveyData] = useState(surveyDataDefaults);
  const [surveyFieldsOK, setSurveyFieldsOK] = useState(false);
  const [view, setView] = useState('project-form');
  const [mainNotice, setMainNotice] = useState({ type: '', message: '' });
  const [showMainNotice, setShowMainNotice] = useState(false);
  const [requiredFieldsOK, setRequiredFieldsOK] = useState(true);
  const [isNewProject, setIsNewProject] = useState(true);
  const [readyForSubmittal, setReadyForSubmittal] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(
    projectData.new_messages
  );
  const [completionDateWrong, setCompletionDateWrong] = useState(false);
  const [projectDeleted, setProjectDeleted] = useState(false);
  const [attestationError, setAttestationError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [manuallyShowSurveyModal, setManuallyShowSurveyModal] = useState(false);

  const introText = window.wlPipFormMeta.intro;
  const canCreateNewProject = window.hasOwnProperty('wlPipProjectError')
    ? !!window.wlPipProjectError.canCreateNewProject
    : false;

  useEffect(() => {
    // manually show survey modal for builders who should have seen it but submitted projects before the modal was implemented
    const searchParams = new URLSearchParams(window.location.search);
    const paramValue = searchParams.get('survey');
    
    if (paramValue === 'show_survey') { 
      setLoading(true);
      axios({
          method: 'post',
          url: `${window.wlPipConfig.ajaxUrl}?action=wl_pip_get_projects`
        })
          .then(({ data: { success, data } }) => {
            setLoading(false);
            if (success) {
              const surveyCompleted = data.some(
                (project) => project.date_submitted && project.date_submitted.includes('2024')
              );
              if (!surveyCompleted) {
                setManuallyShowSurveyModal(true);
                setIsModalOpen(true);
              }
            }
          })
          .catch((error) => {
            setLoading(false);
          });
    }
  }, []);

  const getPageTitle = () => {
    const title = projectData.address;

    if (isNewProject) {
      return title === '' ? 'Add a Project' : title;
    }

    switch (view) {
      case 'document-epat':
        return `${title}: EPAT`;
      case 'propane-provider':
        return `${title}: Propane Provider`;
      case 'appliances':
        return `${title}: Appliances`;
      case 'messages':
        return `${title}: Messages`;
      case 'project-form':
      default:
        return title;
    }
  };

  const getIntroText = () => {
    switch (view) {
      case 'document-epat':
        return introText['epat'];
      case 'propane-provider':
        return introText['gas'];
      case 'appliances':
        return introText['appliances'];
      case 'project-form':
      default:
        return introText['basic'];
    }
  };

  const displayCurrentView = () => {
    switch (view) {
      case 'appliances':
        return (
          <Appliances
            projectData={projectData}
            handleInputChange={handleInputChange}
            handleChangeView={handleChangeView}
            handleSaveProject={handleSaveProject}
            updateSpecificProjectDataProperty={
              updateSpecificProjectDataProperty
            }
          />
        );

      case 'document-epat':
        return (
          <Epat
            projectData={projectData}
            handleInputChange={handleInputChange}
            handleChangeView={handleChangeView}
            handleSaveProject={handleSaveProject}
            updateSpecificProjectDataProperty={
              updateSpecificProjectDataProperty
            }
          />
        );

      case 'propane-provider':
        return (
          <PropaneProvider
            projectData={projectData}
            handleChangeView={handleChangeView}
            handleSaveProject={handleSaveProject}
            updateSpecificProjectDataProperty={
              updateSpecificProjectDataProperty
            }
          />
        );

      case 'messages':
        return (
          <Messages
            projectData={projectData}
            setUnreadMessages={setUnreadMessages}
          />
        );

      case 'project-form':
      default:
        return (
          <ProjectForm
            projectData={projectData}
            unreadMessages={unreadMessages}
            requiredFieldsOK={requiredFieldsOK}
            epatFieldsOK={epatFieldsOK}
            isNewProject={isNewProject}
            handleInputChange={handleInputChange}
            handleChangeView={handleChangeView}
            completionDateWrong={completionDateWrong}
            updateSpecificProjectDataProperty={
              updateSpecificProjectDataProperty
            }
          />
        );
    }
  };

  const checkRequiredFields = () => {
    let {
      construction_type,
      address,
      city,
      state,
      zipcode,
      completion_date,
      appliance_attestation
    } = projectData;

    if (
      !construction_type ||
      !address ||
      !city ||
      !state ||
      zipcode.replace(/_/gi, '').length !== 5 ||
      completion_date.replace(/_/gi, '').replace(/\//gi, '').length !== 8
    ) {
      setRequiredFieldsOK(false);
      return false;
    }

    if (isNewProject && appliance_attestation !== 1) {
      setRequiredFieldsOK(false);
      setAttestationError(true);
      return false;
    }

    setRequiredFieldsOK(true);
    return true;
  };

  const checkSurveyRequiredFields = () => {
    const {
      building_cost,
      sell_speed,
      sell_cost,
      propane_barrier,
      home_buyer_involvement,
      energy_decisions,
      environmental_benefits,
      energy_planning_analysis_tool,
      emissions_benefits,
      propane_construction_research_program,
      may_we_contact_you
    } = surveyData;

    if (
      !building_cost ||
      !sell_speed ||
      !sell_cost ||
      !propane_barrier.length ||
      !home_buyer_involvement ||
      !energy_decisions.length ||
      !environmental_benefits ||
      !energy_planning_analysis_tool ||
      !emissions_benefits ||
      !propane_construction_research_program ||
      !may_we_contact_you
    ) {
      return false;
    }

    return true;
  };

  const epatFieldsOK = () => {
    const epatTableFields = ['so2', 'nox', 'co2', 'ch4', 'n2o', 'co2e'];
    let errors = 0;

    epatTableFields.map((field) =>
      Array.from(['alternative', 'baseline']).map((property) => {
        const propertyName = `epat_emissions_${property}_${field}`;
        if (
          projectData[propertyName] === '' ||
          projectData[propertyName] == '0'
        )
          errors++;
      })
    );

    return errors === 0;
  };

  const handleInputChange = (event) => {
    let newProjectData = projectData;

    // CompletionDate specific validation.
    if (event.currentTarget.name === 'completion_date') {
      setCompletionDateWrong(!isCompletionDateCorrect(event));
    }

    newProjectData[event.currentTarget.name] = event.currentTarget.value;
    setProjectData({ ...newProjectData });

    checkRequiredFields();
  };

  const handleSurveyInputChange = (event) => {
    const { name, value, checked, type } = event.target;

    if (type === 'checkbox') {
      let updatedValues = [...(surveyData[name] || [])];

      if (checked) {
        updatedValues = [...updatedValues, value];
      } else {
        updatedValues = updatedValues.filter((item) => item !== value);
      }

      setSurveyData((prevState) => ({
        ...prevState,
        [name]: updatedValues
      }));
    } else {
      setSurveyData((prevState) => ({
        ...prevState,
        [name]: value
      }));
    }

    setSurveyFieldsOK(checkSurveyRequiredFields());
  };

  useEffect(() => {
    setSurveyFieldsOK(checkSurveyRequiredFields());
  }, [surveyData]);

  const handleChangeView = (view) => {
    setLoading(true);
    setShowMainNotice(false);

    setTimeout(() => {
      setLoading(false);
      view ? setView(view) : setView('project-form');
    }, Math.random() * 250);
  };

  const handleSaveProject = () => {
    setLoading(true);

    if (!checkRequiredFields()) {
      setMainNotice({
        type: 'error',
        message: attestationError
          ? 'Appliance Attestation Required'
          : 'Required fields can not be empty.'
      });
      setShowMainNotice(true);

      // Switch to 'project-form' view, since these required fields live there..
      if (view !== 'project-form') {
        setView('project-form');
      }

      setTimeout(() => setLoading(false), 250);
      return;
    }

    axios
      .post(
        window.wlPipConfig.ajaxUrl,
        qs.stringify({ action: 'wl_pip_save_project', ...projectData })
      )
      .then(({ data: { success, data } }) => {
        if (isNewProject && data.redirect) {
          window.location.href = data.redirect;
          return;
        }

        setMainNotice({
          type: success ? 'success' : 'error',
          message: data.message
        });
        setShowMainNotice(true);

        if (data.meta_fields) {
          setProjectData(data.meta_fields);
        }

        // hide 2 week notice if project was updated
        if (data.updated) {
          let notice = document.querySelector('.project-inactive');
          notice && notice.classList.add('u--display-none');
        }

        setTimeout(() => setLoading(false), 250);
      })
      .catch(({ data: { success, data } }) => {
        setMainNotice({
          type: success ? 'success' : 'error',
          message: data.message
        });
        setShowMainNotice(true);

        setTimeout(() => setLoading(false), 250);
      });
  };

  const handleSubmitProject = () => {
    setLoading(true);

    if (!readyForSubmittal) {
      setMainNotice({
        type: 'error',
        message:
          'Project is not ready for submittal yet. Please fill out to missing fields and uploads and missing documents.'
      });
      setShowMainNotice(true);

      // Switch to 'project-form' view, since these required fields live there..
      if (view !== 'project-form') {
        setView('project-form');
      }

      setLoading(false);
      return;
    }

    // if completion date is in the future, don't allow submit
    const now = new Date();
    const completionDateToCheck = new Date(projectData.completion_date);

    if (completionDateToCheck > now) {
      setMainNotice({
        type: 'error',
        message:
          'Project cannot be submitted before the expected completion date.'
      });
      setShowMainNotice(true);
      setLoading(false);
      return;
    }

    axios
      .post(
        window.wlPipConfig.ajaxUrl,
        qs.stringify({ action: 'wl_pip_submit_project', pid: projectData.pid })
      )
      .then(({ data: { success, data } }) => {
        if (data.showSurvey) {
          setIsModalOpen(true);
          setLoading(false);
          return;
        } else {
          setMainNotice({
            type: success ? 'success' : 'error',
            message: data.message
          });
          setShowMainNotice(true);
          if (success) {
            updateSpecificProjectDataProperty('status', 'Submitted');
          }
        }

        setTimeout(() => setLoading(false), 250);
      })
      .catch((error) => {
        setMainNotice({
          type: 'error',
          message: error
        });
        setShowMainNotice(true);

        setTimeout(() => setLoading(false), 250);
      });
  };

  const handleSurveySubmit = () => {
    // Create a copy of surveyData and change any arrays to comma separated strings
    const submissionData = Object.entries(surveyData).reduce(
      (acc, [key, value]) => {
        acc[key] = Array.isArray(value) ? value.join(', ') : value;
        return acc;
      },
      {}
    );

    axios
      .post(
        window.wlPipConfig.ajaxUrl,
        qs.stringify({ action: 'wl_pip_save_survey', ...submissionData })
      )
      .then(({ data: { success } }) => {
        if (success) {
          setIsModalOpen(false);
      
          if (!manuallyShowSurveyModal) {
            handleSubmitProject();
          }
        }
      });
  };

  const handleDeleteProject = () => {
    setLoading(true);
    axios
      .post(
        window.wlPipConfig.ajaxUrl,
        qs.stringify({ action: 'wl_pip_delete_project', pid: projectData.pid })
      )
      .then(({ data: { success, data } }) => {
        setProjectDeleted(true);
        setLoading(false);
        setMainNotice({
          type: success ? 'success' : 'error',
          message: data.message
        });
        setShowMainNotice(true);

        setTimeout(() => location.reload(), 1000);
      })
      .catch((error) => {
        setMainNotice({
          type: 'error',
          message: error
        });
        setShowMainNotice(true);

        setTimeout(() => setLoading(false), 250);
      });
  };

  const updateSpecificProjectDataProperty = (key, value) => {
    let newProjectData = projectData;

    if (newProjectData.hasOwnProperty(key)) {
      newProjectData[key] = value;
      setProjectData({ ...newProjectData });
    }
  };

  useEffect(() => {
    // Determine whether we're editing a post, or creating a new one.
    if (window.wlPipProject === undefined) return setLoading(false);

    setProjectData(window.wlPipProject);
    setUnreadMessages(window.wlPipProject.new_messages);
    setIsNewProject(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (projectData.firstView == 1 && view === 'project-form') {
      setMainNotice({
        type: 'success',
        message: (
          <>
            Project successfully created. Please continue your application below
            by completing the{' '}
            <span onClick={() => handleChangeView('appliances')}>
              <em>Appliances</em>
            </span>
            ,{' '}
            <span onClick={() => handleChangeView('propane-provider')}>
              <em>Gas Check</em>
            </span>
            , and{' '}
            <span onClick={() => handleChangeView('document-epat')}>
              <em>EPAT</em>
            </span>{' '}
            sections.
          </>
        )
      });
      setShowMainNotice(true);
    }

    if (projectData.status === 'Inactive' && view === 'project-form') {
      setMainNotice({
        type: 'error',
        message: (
          <p>
            This project hasn't been updated in 90 days and is now Inactive. If
            this project is still under construction and is scheduled to be
            completed this year, please message PERC using the “Project
            Messages” tab below and request the project status be changed to In
            Progress.
          </p>
        )
      });
      setShowMainNotice(true);
    }
  }, [projectData]);

  // Check if project is ready for submittal
  useEffect(() => {
    // Basic project details
    const basicFields = requiredFieldsOK;

    // Appliances
    const appliances =
      projectData.appliance_total_incentives !== '' &&
      projectData.appliance_total_incentives != '0' &&
      projectData.appliance_attestation == 1;

    // Gas document
    const doc_gas = () => {
      return (
        projectData.gas_rep_company_name &&
        projectData.gas_rep_representative &&
        projectData.gas_rep_city &&
        projectData.gas_rep_state
      );
    };

    // EPAT fields
    const epat = epatFieldsOK();

    // Builder W-9
    const doc_w9 =
      (projectData.user_builder_w9 !== '' &&
        projectData.user_builder_w9 != '0') ||
      (projectData.has_uploaded_w9 !== '' &&
        projectData.has_uploaded_w9 != '0');

    // Builder W-9
    const signedContract =
      projectData.signed_contract !== '' && projectData.signed_contract != '0';

    // Already submitted
    const alreadySubmitted = projectData.status !== 'Submitted';

    // Check all of the above..
    const projectIsReady =
      basicFields &&
      appliances &&
      doc_gas &&
      epat &&
      doc_w9 &&
      signedContract &&
      alreadySubmitted;

    // ..and set state.
    setReadyForSubmittal(projectIsReady);
  }, [projectData]);

  useEffect(() => {
    if (showMainNotice === false) return;

    setLoading(false);
    window.scrollTo(0, 0);
  }, [showMainNotice]);

  if (isNewProject && !canCreateNewProject) {
    return '';
  }

  if (loading === true || projectData === null) return <Loader />;

  return (
    <>
      {/* Back to {...} link */}
      {isNewProject ||
        (view === 'project-form' && (
          <h5
            className="edit-form__back"
            onClick={() => (window.location.href = '/pcip/dashboard/')}
          >
            Back to Dashboard
          </h5>
        ))}
      {view !== 'project-form' && (
        <h5
          className="edit-form__back"
          onClick={() => {
            handleChangeView();
          }}
        >
          Back to Project Overview
        </h5>
      )}

      {/* Show view's title */}
      <h1 className="u--margin-top-0 u--color-blue-dark">{getPageTitle()}</h1>

      {/* Show view's intro text, if available */}
      {getIntroText() !== '' && (
        <>
          <p dangerouslySetInnerHTML={{ __html: getIntroText() }} />
          {view === 'document-epat' && (
            <p>
              Check out {' '}
              <a
                style={{ cursor: 'pointer' }}
                onClick={(event) => {
                  event.preventDefault();
                  $('[data-modal="epat-modal"]').trigger('modal:show');
                }}
              >
                this instructional video
              </a>{' '}
              for more information on completing this section of your application.
            </p>
          )}
          <hr />
        </>
      )}

      {/* Show notice message */}
      {showMainNotice === true && (
        <div
          className={`notice notice--${mainNotice.type}`}
          role="alert"
          style={{ maxWidth: '600px' }}
        >
          {mainNotice.message}
        </div>
      )}

      {!projectDeleted && (
        <>
          {/* Show whatever view we're on */}
          {displayCurrentView()}

          {/* Save/Submit btns */}
          {view === 'project-form' && (
            <>
              <hr />
              <button
                id="pip-add__save"
                className="btn btn--gold btn--lg module-hover-root u--margin-right-30-sm-min u--margin-bottom-20"
                type="button"
                disabled={
                  completionDateWrong === true ||
                  ['Submitted', 'Approved', 'Completed'].includes(
                    projectData.status
                  )
                }
                onClick={() => handleSaveProject()}
              >
                <span className="btn__content">
                  <span className="btn__text">
                    {isNewProject === true ? 'Create' : 'Save'} Project
                  </span>
                </span>
              </button>
              {isNewProject === false && (
                <button
                  className={`btn btn--lg module-hover-root ${
                    readyForSubmittal ? 'btn--gold' : 'btn--white'
                  } u--margin-right-30-sm-min u--margin-bottom-20`}
                  type="button"
                  disabled={
                    projectData.status === 'Inactive'
                      ? 'disabled'
                      : readyForSubmittal === false
                  }
                  onClick={() => handleSubmitProject()}
                >
                  <span className="btn__content">
                    <span className="btn__text">
                      {projectData.status === 'Submitted'
                        ? 'Project is under review'
                        : 'Submit for Review'}
                    </span>
                  </span>
                </button>
              )}
              {!isNewProject && (
                <DeleteProject handleDeleteProject={handleDeleteProject} />
              )}
            </>
          )}
        </>
      )}
      <SurveyModal
        surveyData={surveyData}
        surveyFieldsOK={surveyFieldsOK}
        isOpen={isModalOpen}
        handleSurveyInputChange={handleSurveyInputChange}
        handleSurveySubmit={handleSurveySubmit}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default ViewProject;
